/**
 * useOrchestrationEvents
 *
 * Live, org-scoped subscription to the `cortex_events_log` Postgres table.
 * Provides an initial fetch (TanStack Query) + Supabase Realtime INSERT
 * subscription so the Orchestration Console drawer always shows a fresh,
 * real-time view of pipeline activity.
 *
 * @module useOrchestrationEvents
 */

import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrganizationId } from "@/hooks/useCurrentOrganization";
import { useAuth } from "@/hooks/useAuth";
import { useRealtimeTable, type UseRealtimeTableOptions } from "@/hooks/useRealtimeTable";
import type { CortexEventLogRow } from "@flowstate/types";
import type { RealtimePayload } from "@/types/realtime";
import type { Database } from "@/integrations/supabase/database.types";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { logger } from "@/lib/logger";
import { useLiveQuery } from "@tanstack/react-db";
import { createCortexEventsLogCollection } from "@/lib/db/collections";
import { isElectricConfigured } from "@/lib/electric";
import { redactSecretsDeep } from "@flowstate/console";

// ============================================================================
// CONSTANTS
// ============================================================================

/** Maximum events held in memory. Oldest are discarded when exceeded. */
const MAX_EVENTS = 500;

/** TanStack Query key prefix for the orchestration console. */
const QUERY_KEY = "orchestration-events";

// ============================================================================
// TYPES
// ============================================================================

type SynapticWakeLogRow = Database["public"]["Tables"]["synaptic_wake_log"]["Row"];
type CortexRunRow = Database["public"]["Tables"]["cortex_runs"]["Row"];
type CortexSessionRow = Database["public"]["Tables"]["cortex_sessions"]["Row"];

export interface OrchestrationEventsFilter {
  /** Narrow to these domain values (client-side). */
  domains?: string[];
  /** Narrow to a specific Cortex session ID (client-side). */
  sessionId?: string;
  /** Narrow to events sharing a correlation ID (client-side). */
  correlationId?: string;
}

export interface UseOrchestrationEventsOptions {
  /** Client-side filter applied after receiving events from Realtime/initial fetch. */
  filter?: OrchestrationEventsFilter;
  /** How many rows to fetch on initial load. Defaults to 100. */
  limit?: number;
  /** Disable the hook entirely (e.g. when the drawer is closed). */
  enabled?: boolean;
}

export interface UseOrchestrationEventsResult {
  events: CortexEventLogRow[];
  isLoading: boolean;
  /** True once the Realtime subscription is active. */
  isConnected: boolean;
  /** Clears the in-memory event list (does NOT delete from Postgres). */
  clear: () => void;
}

// ============================================================================
// HELPERS
// ============================================================================

function getDomainRoot(domain: string): string {
  return domain.split(".")[0] ?? domain;
}

function domainMatches(filterDomain: string, eventDomain: string): boolean {
  return eventDomain === filterDomain || getDomainRoot(eventDomain) === filterDomain;
}

function mapWakeToEvent(
  wake: SynapticWakeLogRow,
  orgId?: string | null,
): CortexEventLogRow {
  const sourceName =
    wake.trigger_source === "mycelium_event"
      ? "mycelium-worker"
      : wake.trigger_source === "specialist_complete"
        ? "brain"
        : "unknown";

  return {
    id: `wake-${wake.id}`,
    event_id: wake.pg_net_request_id ? String(wake.pg_net_request_id) : null,
    domain: "system",
    event_type: wake.event_type ?? "brain.wake",
    principal_id: null,
    user_id: null,
    org_id: orgId ?? "",
    correlation_id: wake.run_id ?? null,
    causation_id: null,
    cortex_session_id: wake.session_id ?? null,
    cortex_run_id: wake.run_id ?? null,
    priority: 3,
    source: sourceName,
    summary:
      `⚡ [Wake] Brain triggered via ${wake.trigger_source ?? "unknown"} (${wake.event_type ?? "unknown"})` +
      (wake.error ? ` - ❌ Error: ${wake.error}` : " - Succeeded"),
    status: wake.error ? "failed" : "succeeded",
    duration_ms: null,
    payload: {
      id: wake.id,
      trigger_source: wake.trigger_source,
      event_type: wake.event_type,
      pg_net_request_id: wake.pg_net_request_id,
      error: wake.error,
      triggered_at: wake.triggered_at,
    },
    metadata: {},
    occurred_at: wake.triggered_at ?? new Date().toISOString(),
    created_at: wake.triggered_at ?? new Date().toISOString(),
  };
}

function mapRunToEvent(
  run: CortexRunRow,
  orgId?: string | null,
): CortexEventLogRow {
  const status = run.status ?? "unknown";
  const isComplete = status === "complete";
  const isFailed = status === "failed" || status === "timeout";
  const statusType: CortexEventLogRow["status"] = isFailed
    ? "failed"
    : isComplete
      ? "succeeded"
      : status === "pending"
        ? "started"
        : "info";

  let duration: number | null = null;
  if (run.created_at && run.updated_at) {
    duration = Math.max(
      0,
      new Date(run.updated_at).getTime() - new Date(run.created_at).getTime(),
    );
  }

  const checkpoint =
    run.checkpoint && typeof run.checkpoint === "object"
      ? (run.checkpoint as Record<string, unknown>)
      : null;
  const primaryProvider =
    typeof checkpoint?.primaryProvider === "string"
      ? checkpoint.primaryProvider
      : null;
  const primaryModel =
    typeof checkpoint?.primaryModel === "string" ? checkpoint.primaryModel : null;
  const modelLabel =
    primaryProvider && primaryModel
      ? `${primaryProvider}/${primaryModel}`
      : primaryModel ?? primaryProvider;

  return {
    id: `run-${run.id}`,
    event_id: run.id,
    domain: "cortex",
    event_type: `run.status.${status}`,
    principal_id: null,
    user_id: run.user_id ?? null,
    org_id: orgId ?? "",
    correlation_id: null,
    causation_id: null,
    cortex_session_id: run.session_id,
    cortex_run_id: run.id,
    priority: 4,
    source: "brain",
    summary:
      `🧠 [Run] Run state changed to "${status}"` +
      (modelLabel ? ` · ${modelLabel}` : "") +
      (run.specialist_type ? ` (Specialist: ${run.specialist_type})` : "") +
      (run.error_message ? ` - ❌ Error: ${run.error_message}` : "") +
      (duration ? ` in ${duration}ms` : ""),
    status: statusType,
    duration_ms: duration,
    payload: redactSecretsDeep({
      id: run.id,
      status,
      specialist_type: run.specialist_type,
      specialist_result: run.specialist_result,
      error_message: run.error_message,
      primaryProvider,
      primaryModel,
      checkpoint: run.checkpoint,
      created_at: run.created_at,
      updated_at: run.updated_at,
    }) as CortexEventLogRow["payload"],
    metadata: {},
    occurred_at: run.updated_at ?? run.created_at ?? new Date().toISOString(),
    created_at: run.created_at ?? new Date().toISOString(),
  };
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Returns live orchestration events scoped to the current organization.
 *
 * - `cortex_events_log` is the primary, org-scoped feed (Electric or Supabase Realtime).
 * - `cortex_runs` are fetched by `user_id` (covers anon-* leases + chat sessions).
 * - `synaptic_wake_log` is scoped to `chat_sessions` / `cortex_sessions` ids when available.
 * - In-memory list is capped; oldest rows are dropped.
 * - Client-side filters are applied after delivery.
 */
export function useOrchestrationEvents(
  options: UseOrchestrationEventsOptions = {},
): UseOrchestrationEventsResult {
  const { filter, limit = 100, enabled = true } = options;

  const orgId = useCurrentOrganizationId();
  const { user } = useAuth();
  const userId = user?.id;

  // ── Electric Shape Sync + TanStack DB Collection ──────────────────────────
  const eventsCol = useMemo(() => {
    return createCortexEventsLogCollection(orgId ?? undefined);
  }, [orgId]);

  const { data: collectionRows } = useLiveQuery((q) =>
    q.from({ e: eventsCol }).select(({ e }) => e)
  );

  const isElectricReady = isElectricConfigured() && collectionRows !== undefined;

  const electricData = useMemo<CortexEventLogRow[]>(() => {
    if (!collectionRows) return [];
    return (collectionRows as unknown as CortexEventLogRow[])
      .sort(
        (a, b) =>
          new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime(),
      )
      .slice(0, limit);
  }, [collectionRows, limit]);

  // ── Fallback 1: In-memory event list for Supabase Realtime ──────────────────
  const [events, setEvents] = useState<CortexEventLogRow[]>([]);
  const eventsRef = useRef(events);
  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  // ── Fallback 2: Initial fetch via Supabase REST ────────────────────────────
  const { data: initialRows, isLoading: isSupabaseLoading } = useQuery({
    queryKey: [QUERY_KEY, orgId, limit],
    enabled: enabled && !!orgId && !isElectricReady,
    staleTime: 30_000,
    queryFn: async (): Promise<CortexEventLogRow[]> => {
      if (!orgId) return [];

      const { data, error } = await supabase
        .from("cortex_events_log")
        .select("*")
        .eq("org_id", orgId)
        .order("occurred_at", { ascending: false })
        .limit(limit);

      if (error) {
        logger.warn("[useOrchestrationEvents] initial fetch error", { error });
        return [];
      }

      return (data ?? []) as CortexEventLogRow[];
    },
  });

  // Seed state when initial rows arrive. Resets on org switch.
  const seededRef = useRef<string | null>(null);
  useEffect(() => {
    if (initialRows && seededRef.current !== orgId) {
      seededRef.current = orgId;
      setEvents(initialRows);
    }
  }, [initialRows, orgId]);

  // ── Fallback 3: Realtime subscription ─────────────────────────────────────
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);

  const handleInsert = useCallback(
    (payload: RealtimePayload<CortexEventLogRow>) => {
      if (payload.eventType !== "INSERT" || !payload.new) return;
      const newRow = payload.new;

      setEvents((prev) => {
        // Prepend (newest first) and cap
        const updated = [newRow, ...prev];
        if (updated.length > MAX_EVENTS) {
          return updated.slice(0, MAX_EVENTS);
        }
        return updated;
      });
    },
    [],
  );

  const { isSubscribed } = useRealtimeTable("cortex_events_log", {
    enabled: enabled && !!orgId && !isElectricReady,
    filter: orgId
      ? { column: "org_id", operator: "eq", value: orgId }
      : undefined,
    events: ["INSERT"],
    onChange: handleInsert as UseRealtimeTableOptions<"cortex_events_log">["onChange"],
  });

  useEffect(() => {
    setIsRealtimeConnected(isSubscribed);
  }, [isSubscribed]);

  // ── Wakes and Runs secondary feeds ──────────────────────────────────────────
  const [wakes, setWakes] = useState<CortexEventLogRow[]>([]);
  const [runs, setRuns] = useState<CortexEventLogRow[]>([]);
  const [isSecondaryLoading, setIsSecondaryLoading] = useState(false);

  // Resolve session IDs for wake-log scoping.
  // flowstate-start persists conversations in `chat_sessions` and `cortex_sessions`.
  // Brain often falls back to `anon-*` session ids that are in
  // neither table — so runs are also fetched by user_id below.
  const { data: sessionIds, isLoading: isSessionIdsLoading } = useQuery({
    queryKey: ["orchestration-session-ids", userId],
    enabled: enabled && !!userId && !filter?.sessionId,
    staleTime: 60_000,
    queryFn: async (): Promise<string[]> => {
      if (!userId) return [];

      const [chatRes, cortexRes] = await Promise.all([
        supabase
          .from("chat_sessions")
          .select("id")
          .eq("user_id", userId)
          .order("updated_at", { ascending: false })
          .limit(500),
        supabase
          .from("cortex_sessions")
          .select("id")
          .eq("user_id", userId)
          .order("last_active_at", { ascending: false })
          .limit(500),
      ]);

      if (chatRes.error) {
        logger.warn("[useOrchestrationEvents] chat_sessions fetch error", {
          error: chatRes.error,
        });
      }
      if (cortexRes.error) {
        logger.warn("[useOrchestrationEvents] cortex_sessions fetch error", {
          error: cortexRes.error,
        });
      }

      const ids = new Set<string>();
      for (const row of chatRes.data ?? []) ids.add(row.id);
      for (const row of cortexRes.data ?? []) ids.add(row.id);
      return Array.from(ids);
    },
  });

  const targetSessionIds = useMemo<string[] | undefined>(() => {
    if (filter?.sessionId) return [filter.sessionId];
    if (sessionIds === undefined) return undefined;
    return sessionIds;
  }, [filter?.sessionId, sessionIds]);

  // Initial fetch for wakes and runs (which aren't synced via Electric)
  useEffect(() => {
    if (!enabled || !supabase || !userId) return;

    let cancelled = false;

    const fetchWakesAndRuns = async () => {
      const client = supabase;
      if (!client) return;

      // Wait until session-id query settles (unless an explicit session filter).
      if (targetSessionIds === undefined) return;

      setIsSecondaryLoading(true);

      // Runs: prefer user_id — matches anon-* leases and chat_sessions alike.
      const qRuns = client
        .from("cortex_runs")
        .select("*")
        .eq("user_id", userId)
        .order("updated_at", { ascending: false })
        .limit(limit);

      // Wakes: session-scoped when we have ids; otherwise empty (wake rows often
      // lack session_id / user_id).
      const qWakes =
        targetSessionIds.length > 0
          ? client
              .from("synaptic_wake_log")
              .select("*")
              .in("session_id", targetSessionIds)
              .order("triggered_at", { ascending: false })
              .limit(limit)
          : null;

      const [resWakes, resRuns] = await Promise.all([
        qWakes
          ? Promise.resolve(qWakes).catch(() => ({ data: null, error: null }))
          : Promise.resolve({ data: [] as SynapticWakeLogRow[], error: null }),
        Promise.resolve(qRuns).catch(() => ({ data: null, error: null })),
      ]);

      if (cancelled) return;

      if (resRuns.error) {
        logger.warn("[useOrchestrationEvents] cortex_runs fetch error", {
          error: resRuns.error,
        });
      }

      const wakesData = (resWakes.data ?? []).map((w: SynapticWakeLogRow) =>
        mapWakeToEvent(w, orgId),
      );
      let runsData = (resRuns.data ?? []).map((r: CortexRunRow) =>
        mapRunToEvent(r, orgId),
      );

      // Optional client filter: only runs for a specific session.
      if (filter?.sessionId) {
        runsData = runsData.filter((r) => r.cortex_session_id === filter.sessionId);
      }

      if (!cancelled) {
        setWakes(wakesData);
        setRuns(runsData);
        setIsSecondaryLoading(false);
      }
    };

    fetchWakesAndRuns().catch(() => {
      if (!cancelled) setIsSecondaryLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [enabled, userId, targetSessionIds, orgId, limit, filter?.sessionId]);

  // Realtime subscription for wakes and runs.
  // Channel name must be unique per subscription — AppShell console and Cortex
  // Mesh both call this hook; supabase.channel(sameName) returns the existing
  // subscribed channel, and .on() after subscribe() throws. Also true across
  // effect re-runs before removeChannel settles.
  const channelInstanceId = useRef(
    `orch-${Math.random().toString(36).slice(2, 9)}`,
  );

  useEffect(() => {
    if (!enabled || !supabase || !userId) return;

    const channelName = `orchestration-wakes-runs:${channelInstanceId.current}:${Date.now()}:user:${userId}`;
    const channel = supabase.channel(channelName);

    channel.on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "cortex_runs",
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        if (!payload.new) return;
        const row = mapRunToEvent(payload.new as CortexRunRow, orgId);
        if (filter?.sessionId && row.cortex_session_id !== filter.sessionId) {
          return;
        }
        setRuns((prev) => {
          const filtered = prev.filter((e) => e.id !== row.id);
          const next = [row, ...filtered];
          return next.slice(0, limit);
        });
      },
    );

    if (targetSessionIds && targetSessionIds.length > 0) {
      const sessionFilter = `session_id=in.(${targetSessionIds.join(",")})`;
      channel.on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "synaptic_wake_log",
          filter: sessionFilter,
        },
        (payload) => {
          if (!payload.new) return;
          const row = mapWakeToEvent(payload.new as SynapticWakeLogRow, orgId);
          setWakes((prev) => {
            const filtered = prev.filter((e) => e.id !== row.id);
            const next = [row, ...filtered];
            return next.slice(0, limit);
          });
        },
      );
    }

    channel.subscribe();

    return () => {
      if (supabase && channel) {
        supabase.removeChannel(channel).catch(() => {});
      }
    };
  }, [enabled, userId, targetSessionIds, orgId, limit, filter?.sessionId]);

  // ── Resolve active events set ──────────────────────────────────────────────
  const activeEvents = useMemo(() => {
    const base = isElectricReady ? electricData : events;
    return [...base, ...wakes, ...runs]
      .sort(
        (a, b) =>
          new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime(),
      )
      .slice(0, MAX_EVENTS);
  }, [isElectricReady, electricData, events, wakes, runs]);

  // ── Client-side filtering ──────────────────────────────────────────────────
  const filteredEvents = useMemo<CortexEventLogRow[]>(() => {
    if (!filter) return activeEvents;

    return activeEvents.filter((e) => {
      if (filter.domains && filter.domains.length > 0) {
        if (!filter.domains.some((d) => domainMatches(d, e.domain))) return false;
      }
      if (filter.sessionId) {
        if (e.cortex_session_id !== filter.sessionId) return false;
      }
      if (filter.correlationId) {
        if (e.correlation_id !== filter.correlationId) return false;
      }
      return true;
    });
  }, [activeEvents, filter]);

  // ── clear ──────────────────────────────────────────────────────────────────
  const clear = useCallback(() => {
    setEvents([]);
    setWakes([]);
    setRuns([]);
    seededRef.current = null;
  }, []);

  return {
    events: filteredEvents,
    isLoading: isElectricReady
      ? isSessionIdsLoading || isSecondaryLoading
      : isSupabaseLoading || isSessionIdsLoading || isSecondaryLoading,
    isConnected: isElectricReady ? true : isRealtimeConnected,
    clear,
  };
}
